'use client';

import { type ReactNode, useState, useEffect } from 'react';
import { HStack, VStack, Box } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { Badge } from '../../../../../components/badge';
import { BadgeHttp } from '../../../../../components/styled/badge-http';
import {
  Globe02Icon,
  Link01Icon,
  Link02Icon,
  Target03Icon,
} from '@terra/ui-icons';
import type { HITLContentContext } from '../../tool-config-types';
import { useChatEndpoints } from '../../../contexts/ChatEndpointsContext';
import { useResolvedSignalDetails } from '../../../hooks/useResolvedSignalDisplayTitle';
import type { ParsedEndpoint } from '../../ToolCallsRenderers/types';
import { useTranslations } from 'next-intl';
import { SignalStatusBadge } from '../../../../signals';
import { formatCategoryForDisplay } from '../../../../../utils/category-utils';
import type { SignalStatusType } from '@terra/shared-schemas-orchestrator';
import {
  BodyStrong,
  MetaDefault,
  MonoSmall,
} from '../../../../../components/typography';

type UpdateSignalArgs = {
  signalId?: string;
  title?: string;
  stage?: string;
  status?: SignalStatusType;
  category?: string;
  endpointId?: string;
  harEntryId?: string;
  entryPointId?: string;
};

function MetadataRow({ label, value }: { label: string; value: string }) {
  return (
    <HStack gap={2}>
      <Text fontSize="xs" color="fg.muted" minW="72px">
        {label}
      </Text>
      <Text fontSize="xs">{value}</Text>
    </HStack>
  );
}

function ClassificationItem({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <HStack gap={1.5} alignItems="center">
      <Text fontSize="xs" color="fg.muted">
        {label}
      </Text>
      {children}
    </HStack>
  );
}

function UpdateSignalContentInner({ data }: { data: UpdateSignalArgs }) {
  const { fetchEndpoints } = useChatEndpoints();
  const [endpoints, setEndpoints] = useState<ParsedEndpoint[]>([]);
  const t = useTranslations('designSystem.chat.updateSignalContentRenderer');
  const resolvedSignal = useResolvedSignalDetails({
    signalId: data.signalId,
    title: data.title,
  });
  const resolvedSignalTitle = resolvedSignal.title;
  const canShowProposedEndpoint = resolvedSignal.isEndpointEditable === true;

  useEffect(() => {
    if (!canShowProposedEndpoint || !data.endpointId) {
      return;
    }

    fetchEndpoints().then(setEndpoints);
  }, [canShowProposedEndpoint, fetchEndpoints, data.endpointId]);
  const resolvedEndpoint =
    canShowProposedEndpoint && data.endpointId
      ? endpoints.find((ep) => ep.endpointId === data.endpointId)
      : undefined;
  const hasStatusOrCategory = data.status || data.category;
  const hasAssets =
    canShowProposedEndpoint &&
    Boolean(data.endpointId || data.harEntryId || data.entryPointId);
  const proposedEndpointMatchesCurrent =
    Boolean(data.endpointId) && data.endpointId === resolvedSignal.endpointId;
  const endpointDisplayPath =
    resolvedEndpoint?.path ||
    resolvedEndpoint?.endpointUrl ||
    (proposedEndpointMatchesCurrent ? resolvedSignal.url : undefined);
  const endpointDisplayText =
    endpointDisplayPath ||
    resolvedEndpoint?.endpointDescription ||
    data.endpointId;
  const endpointMethod =
    resolvedEndpoint?.method ||
    (proposedEndpointMatchesCurrent ? resolvedSignal.method : undefined);

  return (
    <VStack gap={3} alignItems="stretch" width="100%">
      <Box
        p={2.5}
        bg="bg.subtle"
        borderRadius="md"
        borderWidth="1px"
        borderColor="border.default"
      >
        <VStack alignItems="stretch" gap={1.5}>
          {resolvedSignalTitle ? (
            <HStack gap={2} alignItems="center" minWidth={0}>
              <Target03Icon size={13} style={{ flexShrink: 0 }} />
              <MetaDefault color="fg.muted" flexShrink={0}>
                {t('signalName')}
              </MetaDefault>
              <BodyStrong truncate minWidth={0}>
                {resolvedSignalTitle}
              </BodyStrong>
            </HStack>
          ) : (
            <HStack gap={2} alignItems="center" color="fg.muted">
              <Target03Icon size={13} />
              <MetaDefault color="fg.muted">{t('signalId')}</MetaDefault>
              <MonoSmall>{data.signalId}</MonoSmall>
            </HStack>
          )}
          {resolvedSignalTitle ? (
            <HStack gap={2} alignItems="center" pl={5} minWidth={0}>
              <MetaDefault color="fg.muted" flexShrink={0}>
                {t('signalId')}
              </MetaDefault>
              <MonoSmall truncate minWidth={0}>
                {data.signalId}
              </MonoSmall>
            </HStack>
          ) : null}
        </VStack>
      </Box>

      {hasStatusOrCategory && (
        <VStack alignItems="stretch" gap={2}>
          <Text fontSize="xs" fontWeight="semibold" color="fg.muted">
            {t('statusClassification')}
          </Text>
          <HStack gap={2} flexWrap="wrap">
            {data.status && (
              <ClassificationItem label={t('status')}>
                <SignalStatusBadge status={data.status} />
              </ClassificationItem>
            )}
            {data.category && (
              <ClassificationItem label={t('category')}>
                <Badge variant="fill" size="sm">
                  {formatCategoryForDisplay(data.category)}
                </Badge>
              </ClassificationItem>
            )}
          </HStack>
        </VStack>
      )}

      {hasAssets && (
        <VStack alignItems="stretch" gap={2}>
          <HStack gap={1.5} alignItems="center">
            <Link01Icon size={12} color="var(--colors-fg-muted)" />
            <Text fontSize="xs" fontWeight="semibold" color="fg.muted">
              {t('assetLinks')}
            </Text>
          </HStack>

          <Box
            borderWidth="1px"
            borderColor="border.subtle"
            borderRadius="md"
            overflow="hidden"
          >
            {data.endpointId && (
              <HStack
                gap={2}
                alignItems="center"
                px={2}
                py={1.5}
                borderBottomWidth={
                  data.harEntryId || data.entryPointId ? '1px' : '0'
                }
                borderColor="border.subtle"
                bg="bg.default"
              >
                {endpointMethod && (
                  <BadgeHttp method={endpointMethod} size="sm" flexShrink={0}>
                    <Text fontSize="2xs" fontFamily="mono">
                      {endpointMethod}
                    </Text>
                  </BadgeHttp>
                )}
                <HStack gap={1} alignItems="center" flex={1} minWidth={0}>
                  {endpointDisplayPath ? (
                    <Link02Icon
                      size={10}
                      style={{
                        flexShrink: 0,
                        color: 'var(--colors-fg-subtle)',
                      }}
                    />
                  ) : (
                    <Globe02Icon
                      size={10}
                      style={{
                        flexShrink: 0,
                        color: 'var(--colors-fg-subtle)',
                      }}
                    />
                  )}
                  <Text fontSize="xs" truncate flex={1}>
                    {endpointDisplayText}
                  </Text>
                </HStack>
              </HStack>
            )}

            {(data.harEntryId || data.entryPointId) && (
              <VStack alignItems="start" gap={1} px={3} py={2} bg="bg.subtle">
                {data.harEntryId && (
                  <MetadataRow label={t('harEntry')} value={data.harEntryId} />
                )}
                {data.entryPointId && (
                  <MetadataRow
                    label={t('entryPoint')}
                    value={data.entryPointId}
                  />
                )}
              </VStack>
            )}
          </Box>
        </VStack>
      )}
    </VStack>
  );
}

export const renderUpdateSignalContent = (
  ctx: HITLContentContext,
): ReactNode => {
  const data = ctx.extractedData[0] as UpdateSignalArgs | undefined;

  if (!data?.signalId) return null;

  return <UpdateSignalContentInner data={data} />;
};
